/**
 * Markdown → safe HTML, for the editor preview (TASK-0386) and, later, for Publish
 * (TASK-0387), which pre-renders every `body` into `catalog.json` so the docs site
 * carries no Markdown renderer at runtime.
 *
 * Deliberately a small, closed subset — exactly what the seed converter emits and what
 * the legacy WordPress bodies contained: paragraphs, `**bold**`, `*italic*`, `` `code` ``,
 * `- lists`, `[links](url)` and `## headings`.
 *
 * **Everything is HTML-escaped first.** The input is operator-authored free text that
 * ends up on a public page, so raw HTML in a body must render as visible characters and
 * never as markup — there is no allow-list to get wrong and no sanitiser to keep up to
 * date, because no tag the author writes ever survives.
 *
 * Links are the one place a value reaches an attribute, so their href is filtered to
 * http(s), mailto and same-site relative paths: a `javascript:` href would otherwise be
 * script execution smuggled through an ordinary-looking link.
 */

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** http(s), mailto, or a same-site path. Anything else becomes a non-navigating link. */
export function safeHref(href: string): string | null {
  const value = href.trim();
  if (!value) return null;
  if (/^(https?:|mailto:)/i.test(value)) return value;
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  if (value.startsWith("#")) return value;
  return null;
}

function renderInline(escaped: string): string {
  let html = escaped;
  // Code first: its contents must not then be read as emphasis.
  html = html.replace(/`([^`]+)`/g, (_m, code: string) => `<code>${code}</code>`);
  html = html.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (_m, label: string, href: string) => {
      // The href arrives escaped, so `&amp;` must go back to `&` before it is judged
      // and before it is used — otherwise a perfectly ordinary query string is rejected.
      const raw = href.replace(/&amp;/g, "&").replace(/&#39;/g, "'").replace(/&quot;/g, '"');
      const safe = safeHref(raw);
      if (!safe) return label;
      return `<a href="${escapeHtml(safe)}" rel="noopener noreferrer">${label}</a>`;
    }
  );
  html = html.replace(/\*\*([^*]+)\*\*/g, (_m, text: string) => `<strong>${text}</strong>`);
  html = html.replace(/(^|[^*])\*([^*\n]+)\*/g, (_m, before: string, text: string) => {
    return `${before}<em>${text}</em>`;
  });
  return html;
}

export function renderMarkdown(markdown: string): string {
  if (!markdown?.trim()) return "";

  const blocks = escapeHtml(markdown.replace(/\r\n/g, "\n")).split(/\n\s*\n/);
  const html: string[] = [];

  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;

    const lines = trimmed.split("\n");
    if (lines.every((line) => /^\s*[-*]\s+/.test(line))) {
      const items = lines
        .map((line) => `<li>${renderInline(line.replace(/^\s*[-*]\s+/, ""))}</li>`)
        .join("");
      html.push(`<ul>${items}</ul>`);
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (heading) {
      const level = heading[1].length + 1; // never an <h1>: the page owns that
      html.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      continue;
    }

    html.push(`<p>${renderInline(lines.join("<br />"))}</p>`);
  }

  return html.join("\n");
}
